import { useEffect, useState } from 'react'
import { api, type PromotionCandidate, type Scope, type SourceDocument } from '../api'
import { says } from '../limits'

/**
 * Promoting a repeated limit to a group — TECHNICAL_DESIGN.md §7.1, §5.7.
 *
 * The scope layer was machinery with nothing to drive it. Groups could be
 * created, funds put into them, the blast radius echoed back at approval, every
 * resolved rule tagged with the group it came through — and no screen in the
 * product could author the rule that makes any of it matter. `api.propose` had
 * no callers at all.
 *
 * This is the one act that produces one: a limit the workbook shows on several
 * funds, promoted to the group they belong to. It starts from evidence the
 * import found rather than from a blank form, which is the difference between
 * *this is one policy* being noticed and being invented.
 *
 * **Every refusal in the store is a control here, stated before the click.**
 *
 *   * A scope rule must cite the document that authorises it — `rules.propose`
 *     raises `PromotionNotEvidenced` without one, because "a wrong promotion
 *     takes a limit one client negotiated and imposes it on portfolios whose
 *     mandates never contained it". So the document and clause are required
 *     fields, not optional ones.
 *   * Only the portal channel may attach to a scope (§8.6). Migration and the
 *     mailbox are refused outright, which is why this is a separate deliberate
 *     act on the same screen rather than something staging does.
 *   * It needs `scope.attach_rule`, which an author does not hold. The control
 *     is not shown to somebody who would be refused on arrival.
 *
 * **It proposes; it does not activate, and it does not tidy up.** The result is
 * one proposal on the approval queue, where §5.6 shows the approver exactly
 * which funds it reaches before they can decide. It does not remove the
 * per-fund limits — the screen says so, because a promote that silently
 * replaced them would be doing the thing §7.1 refuses to let migration do.
 */
/**
 * The groups a limit standing on funds of `deskCodes` may be promoted to (decisions 85, 86).
 *
 * A group of the whole firm takes every copy, and is for somebody who works across it.
 * A desk's group takes that desk's copies only, so where the copies span desks each desk's
 * share can go to its own group and the rest stay where they are. The server refuses
 * anything else; this keeps the wrong choice off the list.
 */
export function groupsFor(scopes: Scope[], deskCodes: string[], firmWide: boolean): Scope[] {
  const desks = new Set(deskCodes)
  return scopes.filter((s) => (s.desk_code ? desks.has(s.desk_code) : firmWide))
}

/** The funds a promotion to `scope` stands for: all of them for the firm's group, one desk's for a desk's. */
export function fundsFor(scope: Scope | undefined, all: string[], byDesk: Record<string, string[]>): string[] {
  return scope?.desk_code ? (byDesk[scope.desk_code] ?? []) : all
}

/** How a group is offered: whose it is, and which copies it takes. */
export function groupLabel(scope: Scope, all: string[], byDesk: Record<string, string[]>): string {
  const taken = fundsFor(scope, all, byDesk).length
  return scope.desk_code
    ? `${scope.code} · ${scope.desk_code}'s group — its ${taken} fund${taken === 1 ? '' : 's'}`
    : `${scope.code} · whole firm — all ${taken} funds`
}

export function Promote({
  candidate,
  canAttach,
  firmWide = true,
}: {
  candidate: PromotionCandidate
  canAttach: boolean
  /** Works across the whole firm, and so may promote to the firm's groups. */
  firmWide?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [scopes, setScopes] = useState<Scope[]>([])
  /** The candidate's funds by desk. */
  const [byDesk, setByDesk] = useState<Record<string, string[]>>({})
  const [docs, setDocs] = useState<SourceDocument[]>([])
  const [scopeId, setScopeId] = useState('')
  const [documentId, setDocumentId] = useState('')
  const [clause, setClause] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    Promise.all([api.scopes(), api.documents(), api.portfolios()])
      .then(([s, d, funds]) => {
        setScopes(s)
        setDocs(d.filter((x) => x.status !== 'superseded'))
        const codes = new Set(candidate.portfolio_codes.map((c) => c.toLowerCase()))
        const split: Record<string, string[]> = {}
        for (const f of funds.filter((x) => codes.has(x.code.toLowerCase())))
          (split[f.desk_code ?? ''] ??= []).push(f.code)
        setByDesk(split)
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
  }, [open])

  const said = says(candidate)
  const ready = Boolean(scopeId) && Boolean(documentId) && clause.trim().length > 0

  async function promote() {
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      const scope = scopes.find((s) => s.id === scopeId)!
      const result = await api.propose({
        // Named for the group and what it constrains, not for a fund: this is
        // one policy now, and a key carrying a fund code would say otherwise.
        rule_key: `${scope.code.toLowerCase()}.${candidate.measure ?? candidate.template}`,
        layer: 'house',
        template: candidate.template,
        subject: candidate.subject,
        evaluation_scope: candidate.evaluation_scope,
        machine_checkable: true,
        measure: candidate.measure,
        basis: candidate.basis,
        operator: candidate.operator,
        threshold: candidate.threshold,
        scope_id: scopeId,
        promoted_from_funds: fundsFor(scope, candidate.portfolio_codes, byDesk),
        source_document_id: documentId,
        source_clause: clause.trim(),
        effective_from: new Date().toISOString(),
        // §8.6: the only channel permitted to attach a rule to a scope.
        channel: 'portal',
      })
      setDone(
        `Proposed as ${result.rule_key} on ${scope.code}. An approver decides it, ` +
          'and will be shown every fund it reaches first.',
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <p className="done" data-testid="promoted">
        {done}
      </p>
    )
  }

  if (!canAttach) {
    return (
      <span className="clause" data-testid="cannot-promote">
        An approver promotes this — it puts a limit on every fund in a group.
      </span>
    )
  }

  if (!open) {
    return (
      <button className="ghost" data-testid="promote" onClick={() => setOpen(true)}>
        Promote to a group…
      </button>
    )
  }

  return (
    <form
      className="cite-form"
      data-testid="promote-form"
      onSubmit={(e) => {
        e.preventDefault()
        void promote()
      }}
    >
      <label>
        Group
        <select value={scopeId} required onChange={(e) => setScopeId(e.target.value)}>
          <option value="">Which group?</option>
          {groupsFor(scopes, Object.keys(byDesk), firmWide).map((s) => (
            <option key={s.id} value={s.id}>
              {groupLabel(s, candidate.portfolio_codes, byDesk)}
            </option>
          ))}
        </select>
      </label>
      <label>
        Document
        <select
          value={documentId}
          required
          title="The policy document that authorises this as one rule for the whole group. A scope rule without one is refused."
          onChange={(e) => setDocumentId(e.target.value)}
        >
          <option value="">Which document authorises it?</option>
          {docs.map((d) => (
            <option key={d.id} value={d.id}>
              {d.title}
              {d.version_label ? ` · ${d.version_label}` : ''}
            </option>
          ))}
        </select>
      </label>
      <label>
        Clause
        <input
          value={clause}
          required
          placeholder="§3"
          title="Where in that document this is written."
          onChange={(e) => setClause(e.target.value)}
        />
      </label>
      <button type="submit" disabled={!ready || busy}>
        {busy ? 'Proposing…' : `Propose ${said.text} on the group`}
      </button>
      <button type="button" className="ghost" onClick={() => setOpen(false)}>
        Cancel
      </button>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <p className="note">
        This proposes <strong>one</strong> limit on the group. It does not remove
        the per-fund copies — if you have already staged them, decline them on the
        approval queue. Nothing takes effect until an approver agrees, and they
        are shown every fund the group reaches before they can.
      </p>
    </form>
  )
}
